/**
 * Sector operations — Operational Wage Sheet Service (R39 G6, SO-D41, SO-P107).
 * Typed reads, subscriptions, and callable wrappers for rates, sheets, locks, and CSV exports.
 */

import {
  collection,
  doc,
  getDoc,
  onSnapshot,
  orderBy,
  query,
  where,
  type Unsubscribe,
} from 'firebase/firestore'
import { firestoreDb } from './firebase'
import { callable } from './callable'
import type {
  OpsWageRateDoc,
  OpsWageSheetDoc,
  SetOpsWageRateInput,
  SetOpsWageRateResult,
  PreviewOpsWageSheetInput,
  PreviewOpsWageSheetResult,
  LockOpsWageSheetInput,
  LockOpsWageSheetResult,
  UnlockOpsWageSheetInput,
  UnlockOpsWageSheetResult,
  ExportOpsWageSheetCsvInput,
  ExportOpsWageSheetCsvResult,
  GetOpsWageSheetStatusInput,
  GetOpsWageSheetStatusResult,
} from '../types/ops'

// ---------------------------------------------------------------------------
// Callables
// ---------------------------------------------------------------------------

export async function setOpsWageRate(
  input: SetOpsWageRateInput,
): Promise<SetOpsWageRateResult> {
  return callable<SetOpsWageRateInput, SetOpsWageRateResult>(
    'setOpsWageRate',
    input,
  )
}

export async function previewOpsWageSheet(
  input: PreviewOpsWageSheetInput,
): Promise<PreviewOpsWageSheetResult> {
  return callable<PreviewOpsWageSheetInput, PreviewOpsWageSheetResult>(
    'previewOpsWageSheet',
    input,
  )
}

export async function lockOpsWageSheet(
  input: LockOpsWageSheetInput,
): Promise<LockOpsWageSheetResult> {
  return callable<LockOpsWageSheetInput, LockOpsWageSheetResult>(
    'lockOpsWageSheet',
    input,
  )
}

export async function unlockOpsWageSheet(
  input: UnlockOpsWageSheetInput,
): Promise<UnlockOpsWageSheetResult> {
  return callable<UnlockOpsWageSheetInput, UnlockOpsWageSheetResult>(
    'unlockOpsWageSheet',
    input,
  )
}

export async function exportOpsWageSheetCsv(
  input: ExportOpsWageSheetCsvInput,
): Promise<ExportOpsWageSheetCsvResult> {
  return callable<ExportOpsWageSheetCsvInput, ExportOpsWageSheetCsvResult>(
    'exportOpsWageSheetCsv',
    input,
  )
}

export async function getOpsWageSheetStatus(
  input: GetOpsWageSheetStatusInput,
): Promise<GetOpsWageSheetStatusResult> {
  return callable<GetOpsWageSheetStatusInput, GetOpsWageSheetStatusResult>(
    'getOpsWageSheetStatus',
    input,
  )
}

// ---------------------------------------------------------------------------
// Subscriptions and Reads
// ---------------------------------------------------------------------------

export function subscribeOpsWageRates(
  workspaceId: string,
  onUpdate: (rates: OpsWageRateDoc[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  if (!workspaceId) {
    onUpdate([])
    return () => {}
  }

  const coll = collection(firestoreDb, 'ops_wage_rates')
  const q = query(coll, where('workspaceId', '==', workspaceId))

  return onSnapshot(
    q,
    (snap) => {
      const docs = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      })) as unknown as OpsWageRateDoc[]
      onUpdate(docs)
    },
    (err) => {
      if (onError) onError(err)
    },
  )
}

export function subscribeOpsWageSheets(
  workspaceId: string,
  onUpdate: (sheets: OpsWageSheetDoc[]) => void,
  onError?: (err: Error) => void,
): Unsubscribe {
  if (!workspaceId) {
    onUpdate([])
    return () => {}
  }

  const coll = collection(firestoreDb, 'ops_wage_sheets')
  const q = query(
    coll,
    where('workspaceId', '==', workspaceId),
    orderBy('periodStart', 'desc'),
  )

  return onSnapshot(
    q,
    (snap) => {
      const docs = snap.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      })) as unknown as OpsWageSheetDoc[]
      onUpdate(docs)
    },
    (err) => {
      if (onError) onError(err)
    },
  )
}

export async function getOpsWageRate(
  workspaceId: string,
  personKey: string,
): Promise<OpsWageRateDoc | null> {
  if (!workspaceId || !personKey) return null
  const docRef = doc(firestoreDb, 'ops_wage_rates', `${workspaceId}_${personKey}`)
  const snap = await getDoc(docRef)
  if (!snap.exists()) return null
  return { id: snap.id, ...snap.data() } as unknown as OpsWageRateDoc
}

export async function getOpsWageSheet(
  workspaceId: string,
  sheetId: string,
): Promise<OpsWageSheetDoc | null> {
  if (!workspaceId || !sheetId) return null
  const docRef = doc(firestoreDb, 'ops_wage_sheets', sheetId)
  const snap = await getDoc(docRef)
  if (!snap.exists()) return null
  return { id: snap.id, ...snap.data() } as unknown as OpsWageSheetDoc
}
